import React from 'react';
import { Target, Lightbulb, Users, ExternalLink } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { FOUNDER, COMPANY_VALUES, SWIFT_FINANCE, PARTNERS, IMAGES } from '../data/mock';
import PartnersCarousel from '../components/PartnersCarousel';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const About = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);

  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>{t.meta.about.title}</title>
        <meta name="description" content={t.meta.about.description} />
      </Helmet>
      <section className="bg-gradient-to-br from-[#0a1628] to-[#0f1f38] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t.about.title}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {t.about.subtitle}
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-[#0a1628] mb-6">
              {t.about.ourMission}
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              {t.about.missionText}
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0a1628] mb-4">
              {t.about.ourValues}
            </h2>
            <p className="text-xl text-gray-600">
              {t.about.valuesSubtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {t.about.values.map((value, index) => {
              const icons = [Target, Lightbulb, Users];
              const Icon = icons[index];
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-14 h-14 bg-[#2ecc8a]/10 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-[#2ecc8a]" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0a1628] mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="lg:w-2/5">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={FOUNDER.image}
                  alt={FOUNDER.name}
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/60 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-3xl font-bold text-white mb-1">
                    {FOUNDER.name}
                  </h3>
                  <p className="text-gray-200">{FOUNDER.title}</p>
                </div>
              </div>
            </div>

            <div className="lg:w-3/5">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-6">
                {t.about.meetFounder}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {FOUNDER.bio}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-[#0a1628] to-[#0f1f38]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#2ecc8a]/10 rounded-full mb-6">
              <ExternalLink className="text-[#2ecc8a]" size={32} />
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">
              {t.about.ourPartners}
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-8">
              {t.about.partnersSubtitle}
            </p>
          </div>

          <PartnersCarousel partners={PARTNERS} />
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#0a1628] mb-6">
            {t.about.letsWork}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t.about.letsWorkText}
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-[#2ecc8a] text-white font-semibold rounded-lg hover:bg-[#26b377] transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            {t.about.getInTouch}
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
